/**
 * 週ビュー (FullCalendar) と日ビューで共通の予定アイテム生成
 *
 * 各ビューはこの結果を FullCalendar のイベント / タイムラインのブロックへ変換するだけにする。
 */

import type { SleepPlanWindow } from '@/api/types/sleep'
import type { CalendarEvent, TodoResponse } from '@/api/types/todo'
import type { WorkoutSessionListItem } from '@/api/types/workout'
import { ensureUtc } from '@/lib/date-utils'

export const SCHEDULE_COLORS = {
  calendar: '#475569',
  todo: '#1d4ed8',
  personal: '#ea580c',
  done: '#166534',
  workout: '#dc2626',
  deadline: '#d97706',
  windDown: '#818cf8',
  sleepPlan: '#4338ca',
} as const

export type ScheduleItemKind = Exclude<keyof typeof SCHEDULE_COLORS, 'deadline'>

export type ScheduleItem = {
  id: string
  kind: ScheduleItemKind
  title: string
  icon: string
  /** 補足表示 (日ビューのサブタイトル) */
  subtitle?: string
  /** タイトル末尾に付ける短い注記 (週ビュー) */
  badge?: string
  start: Date
  end: Date
  allDay: boolean
  /** ドラッグ・リサイズで時刻を変更できるか */
  editable: boolean
  todoId?: number
  /** ローカル予定として作られた TODO か (完了時の表示列の判定に使う) */
  isEvent?: boolean
}

type BuildParams = {
  todos: TodoResponse[]
  calendarEvents: CalendarEvent[]
  workoutSessions: WorkoutSessionListItem[]
  sleepWindows: SleepPlanWindow[]
  /** 保留トレイにあるなど、表示しない TODO */
  hiddenTodoIds?: number[]
}

const DEFAULT_DONE_MINUTES = 30

const toDate = (iso: string) => new Date(ensureUtc(iso))

/** 締切表示の対象か */
export const isDeadlineTodo = (t: TodoResponse) => !!t.deadline && t.status !== 'done' && t.status !== 'dismissed'

const fromCalendarEvent = (ev: CalendarEvent): ScheduleItem => ({
  id: `cal-${ev.event_id}`,
  kind: 'calendar',
  title: ev.summary ?? '(無題)',
  icon: '',
  start: toDate(ev.start_at),
  end: toDate(ev.end_at),
  allDay: ev.is_all_day,
  editable: false,
})

const fromScheduledTodo = (t: TodoResponse): ScheduleItem => ({
  id: `todo-${t.id}`,
  kind: t.is_event ? 'personal' : 'todo',
  title: t.title,
  icon: t.is_event ? '📅' : '📝',
  start: toDate(t.scheduled_start as string),
  end: toDate(t.scheduled_end as string),
  allDay: false,
  editable: true,
  todoId: t.id,
  isEvent: t.is_event,
})

/** 完了 TODO は completed_at を終了として見積もり時間ぶん遡って表示する */
const fromCompletedTodo = (t: TodoResponse): ScheduleItem => {
  const end = toDate(t.completed_at as string)
  const durationMs = (t.estimated_minutes ?? DEFAULT_DONE_MINUTES) * 60 * 1000
  return {
    id: `done-${t.id}`,
    kind: 'done',
    title: t.title,
    icon: '✅',
    start: new Date(end.getTime() - durationMs),
    end,
    allDay: false,
    editable: false,
    todoId: t.id,
    isEvent: t.is_event,
  }
}

const fromWorkout = (s: WorkoutSessionListItem): ScheduleItem => ({
  id: `workout-${s.id}`,
  kind: 'workout',
  title: s.template_name ?? s.name ?? '筋トレ',
  icon: '🏋️',
  start: toDate(s.started_at),
  end: toDate(s.ended_at as string),
  allDay: false,
  editable: false,
})

const fromSleepWindow = (w: SleepPlanWindow): ScheduleItem[] => {
  const earlier = w.shift_min <= -60 ? `${Math.round(-w.shift_min / 60)}h早め` : ''
  const badge = [earlier, w.warning ? '⚠️' : ''].filter(Boolean).join(' ')
  return [
    {
      id: `wind-down-${w.bedtime_at}`,
      kind: 'windDown',
      title: '寝る準備',
      icon: '🛏',
      start: toDate(w.wind_down_at),
      end: toDate(w.bedtime_at),
      allDay: false,
      editable: false,
    },
    {
      id: `sleep-plan-${w.bedtime_at}`,
      kind: 'sleepPlan',
      title: '睡眠（目安）',
      icon: '🌙',
      subtitle: w.warning ?? undefined,
      badge: badge || undefined,
      start: toDate(w.bedtime_at),
      end: toDate(w.wake_at),
      allDay: false,
      editable: false,
    },
  ]
}

export const buildScheduleItems = ({
  todos,
  calendarEvents,
  workoutSessions,
  sleepWindows,
  hiddenTodoIds = [],
}: BuildParams): ScheduleItem[] => {
  const scheduled = todos.filter(
    (t) => t.scheduled_start && t.scheduled_end && t.status !== 'done' && !hiddenTodoIds.includes(t.id),
  )
  const completed = todos.filter((t) => t.status === 'done' && t.completed_at)
  const workouts = workoutSessions.filter((s) => s.status === 'completed' && s.ended_at)
  return [
    ...calendarEvents.map(fromCalendarEvent),
    ...scheduled.map(fromScheduledTodo),
    ...completed.map(fromCompletedTodo),
    ...workouts.map(fromWorkout),
    ...sleepWindows.flatMap(fromSleepWindow),
  ]
}

/** 予定アイテムから元の TODO を引く (クリック時に詳細を開く用) */
export const findItemTodo = (todos: TodoResponse[], todoId: number | undefined) =>
  todoId === undefined ? undefined : todos.find((t) => t.id === todoId)
